import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { profileAPI, authAPI } from '../services/api';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  Grid,
  Divider,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Person, Email, Phone, Logout, Visibility, VisibilityOff, Lock as LockIcon } from '@mui/icons-material';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuthStore();

  const [loginInput, setLoginInput] = useState(user?.display_login || '');
  const [loginSaving, setLoginSaving] = useState(false);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwOld, setPwOld] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwCode, setPwCode] = useState('');
  const [pwCodeSent, setPwCodeSent] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [showPwOld, setShowPwOld] = useState(false);
  const [showPwNew, setShowPwNew] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSaveLogin = async () => {
    const value = loginInput.trim().toLowerCase();
    if (value.length < 5 || value.length > 30) {
      setSnackbar({ open: true, message: 'Логин должен быть от 5 до 30 символов', severity: 'error' });
      return;
    }
    if (!/^[a-z0-9_]+$/.test(value)) {
      setSnackbar({ open: true, message: 'Допустимые символы: a-z, 0-9, _', severity: 'error' });
      return;
    }
    try {
      setLoginSaving(true);
      await profileAPI.setDisplayLogin(value);
      await checkAuth();
      setSnackbar({ open: true, message: 'Логин успешно установлен', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.detail || 'Ошибка установки логина', severity: 'error' });
    } finally {
      setLoginSaving(false);
    }
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const pwErrors: string[] = [];
  if (pwNew) {
    if (pwNew.length < 10) pwErrors.push('минимум 10 символов');
    if (!/[A-ZА-ЯЁ]/.test(pwNew)) pwErrors.push('заглавная буква');
    if (!/[a-zа-яё]/.test(pwNew)) pwErrors.push('строчная буква');
    if (!/\d/.test(pwNew)) pwErrors.push('цифра');
    if (!/[!@#$%^&*()_+\-=\[\]{};:"\\|,.<>/?`~]/.test(pwNew)) pwErrors.push('спецсимвол');
  }

  const handleSendPwCode = async () => {
    setPwError('');
    if (user!.has_password && !pwOld) { setPwError('Введите старый пароль'); return; }
    if (!pwNew) { setPwError('Введите новый пароль'); return; }
    if (pwErrors.length > 0) { setPwError(`Пароль не соответствует требованиям: ${pwErrors.join(', ')}`); return; }
    if (pwNew !== pwConfirm) { setPwError('Пароли не совпадают'); return; }
    if (pwOld === pwNew) { setPwError('Новый пароль совпадает со старым'); return; }
    setPwLoading(true);
    try {
      await authAPI.sendVerificationCode(user!.email!, 'password_change');
      setPwCodeSent(true);
      setPwError('');
    } catch (err: any) {
      setPwError(err.response?.data?.detail || 'Ошибка отправки кода');
    } finally {
      setPwLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (!pwCode) { setPwError('Введите код подтверждения'); return; }
    setPwLoading(true);
    try {
      await profileAPI.changePassword({ old_password: pwOld, new_password: pwNew, verification_code: pwCode });
      setSnackbar({ open: true, message: 'Пароль успешно изменён', severity: 'success' });
      setPwDialogOpen(false);
      setPwOld(''); setPwNew(''); setPwConfirm(''); setPwCode(''); setPwCodeSent(false);
    } catch (err: any) {
      setPwError(err.response?.data?.detail || 'Ошибка смены пароля');
    } finally {
      setPwLoading(false);
    }
  };

  const closePwDialog = () => {
    setPwDialogOpen(false);
    setPwOld(''); setPwNew(''); setPwConfirm(''); setPwCode(''); setPwCodeSent(false); setPwError('');
  };
  
  if (!user) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Профиль
        </Typography>
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Пользователь не найден
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        👤 Профиль пользователя
      </Typography>
      
      <Paper sx={{ p: 4, mt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
          <Avatar
            src={user.avatar_url || undefined}
            sx={{ width: 120, height: 120, mb: 2 }}
          >
            {!user.avatar_url && (user.first_name?.[0] || user.email?.[0] || 'U')}
          </Avatar>
          
          <Typography variant="h5" component="h2">
            {user.first_name || user.last_name
              ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
              : 'Пользователь'}
          </Typography>
          
          {user.email && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {user.email}
            </Typography>
          )}
        </Box>
        
        <Divider sx={{ my: 3 }} />

        {/* Уникальный код */}
        {user.user_code && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Ваш уникальный код
            </Typography>
            <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
              #{user.user_code}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Код неизменяемый и используется для поиска
            </Typography>
          </Box>
        )}

        {/* Логин */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Логин (отображается как @логин)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              value={loginInput}
              onChange={(e) => setLoginInput(e.target.value)}
              placeholder="Введите логин"
              helperText="5–30 символов: a-z, 0-9, _"
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleSaveLogin}
              disabled={loginSaving || loginInput.trim() === (user.display_login || '')}
              sx={{ height: 40 }}
            >
              Сохранить
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Person color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Имя
                </Typography>
                <Typography variant="body1">
                  {user.first_name || 'Не указано'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Person color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Фамилия
                </Typography>
                <Typography variant="body1">
                  {user.last_name || 'Не указано'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Email color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {user.email || 'Не указано'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Phone color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Телефон
                </Typography>
                <Typography variant="body1">
                  {user.phone_number || 'Не указано'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/events')}
          >
            К мероприятиям
          </Button>

          <Button
            variant="outlined"
            onClick={() => navigate('/my-data')}
          >
            📊 Мои данные
          </Button>

          <Button
            variant="outlined"
            startIcon={<LockIcon />}
            onClick={() => setPwDialogOpen(true)}
          >
            Изменить пароль
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={<Logout />}
            onClick={handleLogout}
          >
            Выйти
          </Button>
        </Box>
      </Paper>

      <Dialog open={pwDialogOpen} onClose={closePwDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Изменить пароль</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {pwError && <Alert severity="error" onClose={() => setPwError('')}>{pwError}</Alert>}

          {user.has_password && (
            <TextField
              fullWidth
              label="Старый пароль"
              type={showPwOld ? 'text' : 'password'}
              value={pwOld}
              onChange={(e) => setPwOld(e.target.value)}
              disabled={pwCodeSent}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPwOld(!showPwOld)} onMouseDown={(e) => e.preventDefault()} edge="end" size="small">
                      {showPwOld ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <TextField
            fullWidth
            label="Новый пароль"
            type={showPwNew ? 'text' : 'password'}
            value={pwNew}
            onChange={(e) => setPwNew(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            disabled={pwCodeSent}
            size="small"
            helperText="Минимум 10 символов: заглавные и строчные буквы, цифры, спецсимволы"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPwNew(!showPwNew)} onMouseDown={(e) => e.preventDefault()} edge="end" size="small">
                    {showPwNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Подтвердите новый пароль"
            type={showPwNew ? 'text' : 'password'}
            value={pwConfirm}
            onChange={(e) => setPwConfirm(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            disabled={pwCodeSent}
            size="small"
            error={pwConfirm.length > 0 && pwNew !== pwConfirm}
            helperText={pwConfirm.length > 0 && pwNew !== pwConfirm ? 'Пароли не совпадают' : undefined}
          />

          {pwNew && pwErrors.length > 0 && (
            <Alert severity="warning">
              <Typography variant="caption">Требования к паролю: {pwErrors.join(', ')}</Typography>
            </Alert>
          )}

          {!pwCodeSent ? (
            <Button variant="outlined" onClick={handleSendPwCode} disabled={pwLoading}>
              {pwLoading ? 'Отправка...' : 'Отправить код на почту'}
            </Button>
          ) : (
            <>
              <Alert severity="info">Код отправлен на {user?.email}</Alert>
              <TextField
                fullWidth
                label="Код из письма"
                value={pwCode}
                onChange={(e) => setPwCode(e.target.value)}
                disabled={pwLoading}
                size="small"
                helperText="6-значный код"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePwDialog}>Отмена</Button>
          {pwCodeSent && (
            <Button variant="contained" onClick={handleChangePassword} disabled={pwLoading || !pwCode}>
              {pwLoading ? 'Смена...' : 'Сменить пароль'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;
